export type PropDef =
  | { type: 'color'; default: string; label: string }
  | { type: 'colorArray'; count: number; default: string[]; label: string }
  | { type: 'number'; min: number; max: number; step: number; default: number; label: string }
  | { type: 'boolean'; default: boolean; label: string }
  | { type: 'select'; options: string[]; default: string; label: string }
  | { type: 'rgbArray'; default: [number, number, number]; label: string };

import { BRAND_PINK } from "@/lib/brand";

export interface BackgroundEntry {
  id: string;
  name: string;
  propDefs: Record<string, PropDef>;
  brandDefaults: Record<string, unknown>;
  enabledForProduction: boolean;
  /**
   * True when the component needs a WebGL context (three.js / ogl). The
   * renderer probes for one first and falls back to the CI gradient, so a
   * client that refuses WebGL never reaches `new WebGLRenderer(...)`.
   */
  requiresWebGL: boolean;
}

// Brand CI colors. The pink is the logo colour, from the one place it is
// defined for runtime consumers — see lib/brand.ts.
const CI = {
  hotPink: BRAND_PINK,
  darkCharcoal: '#3d4557',
  deepWine: '#3d1028',
  darkNavy: '#1a1028',
  white: '#ffffff',
  black: '#000000',
} as const;

export const backgrounds: BackgroundEntry[] = [
  {
    id: 'aurora',
    name: 'Aurora',
    propDefs: {
      colorStops: { type: 'colorArray', count: 3, default: [CI.hotPink, CI.deepWine, CI.hotPink], label: 'Color Stops' },
      amplitude: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Amplitude' },
      blend: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.5, label: 'Blend' },
      speed: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Speed' },
    },
    brandDefaults: { colorStops: [CI.hotPink, CI.deepWine, CI.hotPink], amplitude: 1.0, blend: 0.5, speed: 1.0 },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'silk',
    name: 'Silk',
    propDefs: {
      speed: { type: 'number', min: 0, max: 20, step: 0.5, default: 5, label: 'Speed' },
      scale: { type: 'number', min: 0.1, max: 5, step: 0.1, default: 1, label: 'Scale' },
      color: { type: 'color', default: CI.hotPink, label: 'Color' },
      noiseIntensity: { type: 'number', min: 0, max: 5, step: 0.1, default: 1.5, label: 'Noise Intensity' },
      rotation: { type: 'number', min: -180, max: 180, step: 1, default: 0, label: 'Rotation' },
    },
    brandDefaults: { speed: 5, scale: 1, color: CI.hotPink, noiseIntensity: 1.5, rotation: 0 },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'liquidEther',
    name: 'Liquid Ether',
    propDefs: {
      colors: { type: 'colorArray', count: 3, default: [CI.hotPink, CI.deepWine, CI.darkCharcoal], label: 'Colors' },
      mouseForce: { type: 'number', min: 0, max: 50, step: 1, default: 20, label: 'Mouse Force' },
      cursorSize: { type: 'number', min: 10, max: 500, step: 10, default: 100, label: 'Cursor Size' },
      resolution: { type: 'number', min: 0.1, max: 1, step: 0.1, default: 0.5, label: 'Resolution' },
      autoSpeed: { type: 'number', min: 0, max: 3, step: 0.1, default: 0.5, label: 'Auto Speed' },
      autoIntensity: { type: 'number', min: 0, max: 5, step: 0.1, default: 2.2, label: 'Auto Intensity' },
      isViscous: { type: 'boolean', default: true, label: 'Viscous' },
      isBounce: { type: 'boolean', default: false, label: 'Bounce Edges' },
      autoDemo: { type: 'boolean', default: true, label: 'Auto Animate' },
    },
    brandDefaults: { colors: [CI.hotPink, CI.deepWine, CI.darkCharcoal], mouseForce: 20, cursorSize: 100, resolution: 0.5, autoSpeed: 0.5, autoIntensity: 2.2, isViscous: true, isBounce: false, autoDemo: true },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'darkVeil',
    name: 'Dark Veil',
    propDefs: {
      speed: { type: 'number', min: 0, max: 3, step: 0.1, default: 0.5, label: 'Speed' },
      hueShift: { type: 'number', min: -180, max: 180, step: 1, default: 0, label: 'Hue Shift' },
      noiseIntensity: { type: 'number', min: 0, max: 2, step: 0.05, default: 0, label: 'Noise Intensity' },
      scanlineFrequency: { type: 'number', min: 0, max: 200, step: 1, default: 0, label: 'Scanline Frequency' },
      scanlineIntensity: { type: 'number', min: 0, max: 1, step: 0.05, default: 0, label: 'Scanline Intensity' },
      warpAmount: { type: 'number', min: 0, max: 2, step: 0.05, default: 0, label: 'Warp Amount' },
    },
    brandDefaults: { speed: 0.5, hueShift: 0, noiseIntensity: 0, scanlineFrequency: 0, scanlineIntensity: 0, warpAmount: 0 },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'lightPillar',
    name: 'Light Pillar',
    propDefs: {
      topColor: { type: 'color', default: CI.hotPink, label: 'Top Color' },
      bottomColor: { type: 'color', default: CI.deepWine, label: 'Bottom Color' },
      intensity: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Intensity' },
      rotationSpeed: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.3, label: 'Rotation Speed' },
      glowAmount: { type: 'number', min: 0, max: 0.05, step: 0.001, default: 0.005, label: 'Glow Amount' },
      pillarWidth: { type: 'number', min: 0.5, max: 10, step: 0.1, default: 3.0, label: 'Pillar Width' },
      pillarHeight: { type: 'number', min: 0.1, max: 2, step: 0.05, default: 0.4, label: 'Pillar Height' },
      noiseIntensity: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.5, label: 'Noise Intensity' },
      interactive: { type: 'boolean', default: false, label: 'Interactive' },
    },
    brandDefaults: { topColor: CI.hotPink, bottomColor: CI.deepWine, intensity: 1.0, rotationSpeed: 0.3, glowAmount: 0.005, pillarWidth: 3.0, pillarHeight: 0.4, noiseIntensity: 0.5, interactive: false },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'floatingLines',
    name: 'Floating Lines',
    propDefs: {
      animationSpeed: { type: 'number', min: 0, max: 5, step: 0.1, default: 1, label: 'Animation Speed' },
      interactive: { type: 'boolean', default: true, label: 'Interactive' },
      bendRadius: { type: 'number', min: 0.5, max: 20, step: 0.5, default: 5.0, label: 'Bend Radius' },
      bendStrength: { type: 'number', min: -2, max: 2, step: 0.1, default: -0.5, label: 'Bend Strength' },
      parallax: { type: 'boolean', default: true, label: 'Parallax' },
      parallaxStrength: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.2, label: 'Parallax Strength' },
    },
    brandDefaults: { animationSpeed: 1, interactive: true, bendRadius: 5.0, bendStrength: -0.5, parallax: true, parallaxStrength: 0.2 },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'pixelBlast',
    name: 'Pixel Blast',
    propDefs: {
      color: { type: 'color', default: CI.hotPink, label: 'Color' },
      speed: { type: 'number', min: 0, max: 3, step: 0.1, default: 0.5, label: 'Speed' },
      pixelSize: { type: 'number', min: 1, max: 10, step: 1, default: 3, label: 'Pixel Size' },
      patternScale: { type: 'number', min: 0.5, max: 5, step: 0.1, default: 2, label: 'Pattern Scale' },
      patternDensity: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Pattern Density' },
      liquid: { type: 'boolean', default: false, label: 'Liquid' },
      liquidStrength: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.1, label: 'Liquid Strength' },
      enableRipples: { type: 'boolean', default: true, label: 'Enable Ripples' },
      edgeFade: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.5, label: 'Edge Fade' },
      variant: { type: 'select', options: ['square', 'circle', 'triangle'], default: 'square', label: 'Variant' },
    },
    brandDefaults: { color: CI.hotPink, speed: 0.5, pixelSize: 3, patternScale: 2, patternDensity: 1, liquid: false, liquidStrength: 0.1, enableRipples: true, edgeFade: 0.5, variant: 'square' },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'colorBends',
    name: 'Color Bends',
    propDefs: {
      colors: { type: 'colorArray', count: 3, default: [CI.hotPink, CI.deepWine, CI.darkNavy], label: 'Colors' },
      rotation: { type: 'number', min: -180, max: 180, step: 1, default: 45, label: 'Rotation' },
      speed: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.2, label: 'Speed' },
      scale: { type: 'number', min: 0.1, max: 5, step: 0.1, default: 1, label: 'Scale' },
      frequency: { type: 'number', min: 0.1, max: 5, step: 0.1, default: 1, label: 'Frequency' },
      warpStrength: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Warp Strength' },
      noise: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.1, label: 'Noise' },
      parallax: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.5, label: 'Parallax' },
      transparent: { type: 'boolean', default: true, label: 'Transparent' },
    },
    brandDefaults: { colors: [CI.hotPink, CI.deepWine, CI.darkNavy], rotation: 45, speed: 0.2, scale: 1, frequency: 1, warpStrength: 1, noise: 0.1, parallax: 0.5, transparent: true },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'lineWaves',
    name: 'Line Waves',
    propDefs: {
      color1: { type: 'color', default: CI.hotPink, label: 'Color 1' },
      color2: { type: 'color', default: CI.deepWine, label: 'Color 2' },
      color3: { type: 'color', default: CI.darkCharcoal, label: 'Color 3' },
      speed: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.3, label: 'Speed' },
      innerLineCount: { type: 'number', min: 4, max: 64, step: 1, default: 32, label: 'Inner Line Count' },
      outerLineCount: { type: 'number', min: 4, max: 64, step: 1, default: 36, label: 'Outer Line Count' },
      warpIntensity: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Warp Intensity' },
      rotation: { type: 'number', min: -180, max: 180, step: 1, default: -45, label: 'Rotation' },
      brightness: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.2, label: 'Brightness' },
      enableMouseInteraction: { type: 'boolean', default: true, label: 'Mouse Interaction' },
    },
    brandDefaults: { color1: CI.hotPink, color2: CI.deepWine, color3: CI.darkCharcoal, speed: 0.3, innerLineCount: 32, outerLineCount: 36, warpIntensity: 1.0, rotation: -45, brightness: 0.2, enableMouseInteraction: true },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'softAurora',
    name: 'Soft Aurora',
    propDefs: {
      color1: { type: 'color', default: CI.darkCharcoal, label: 'Color 1' },
      color2: { type: 'color', default: CI.hotPink, label: 'Color 2' },
      speed: { type: 'number', min: 0, max: 3, step: 0.1, default: 0.6, label: 'Speed' },
      scale: { type: 'number', min: 0.1, max: 5, step: 0.1, default: 1.5, label: 'Scale' },
      brightness: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Brightness' },
      noiseFrequency: { type: 'number', min: 0.1, max: 10, step: 0.1, default: 2.5, label: 'Noise Frequency' },
      noiseAmplitude: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Noise Amplitude' },
      enableMouseInteraction: { type: 'boolean', default: true, label: 'Mouse Interaction' },
    },
    brandDefaults: { color1: CI.darkCharcoal, color2: CI.hotPink, speed: 0.6, scale: 1.5, brightness: 1.0, noiseFrequency: 2.5, noiseAmplitude: 1.0, enableMouseInteraction: true },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'plasma',
    name: 'Plasma',
    propDefs: {
      color: { type: 'color', default: CI.hotPink, label: 'Color' },
      speed: { type: 'number', min: 0, max: 5, step: 0.1, default: 1, label: 'Speed' },
      scale: { type: 'number', min: 0.1, max: 5, step: 0.1, default: 1, label: 'Scale' },
      opacity: { type: 'number', min: 0, max: 1, step: 0.05, default: 1, label: 'Opacity' },
      direction: { type: 'select', options: ['forward', 'backward'], default: 'forward', label: 'Direction' },
      mouseInteractive: { type: 'boolean', default: true, label: 'Mouse Interactive' },
    },
    brandDefaults: { color: CI.hotPink, speed: 1, scale: 1, opacity: 1, direction: 'forward', mouseInteractive: true },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'grainient',
    name: 'Grainient',
    propDefs: {
      color1: { type: 'color', default: CI.hotPink, label: 'Color 1' },
      color2: { type: 'color', default: CI.deepWine, label: 'Color 2' },
      color3: { type: 'color', default: CI.darkNavy, label: 'Color 3' },
      timeSpeed: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.25, label: 'Time Speed' },
      warpStrength: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Warp Strength' },
      grainAmount: { type: 'number', min: 0, max: 0.5, step: 0.01, default: 0.1, label: 'Grain Amount' },
      contrast: { type: 'number', min: 0.5, max: 3, step: 0.1, default: 1.5, label: 'Contrast' },
      saturation: { type: 'number', min: 0, max: 3, step: 0.1, default: 1.0, label: 'Saturation' },
      zoom: { type: 'number', min: 0.1, max: 3, step: 0.1, default: 0.9, label: 'Zoom' },
    },
    brandDefaults: { color1: CI.hotPink, color2: CI.deepWine, color3: CI.darkNavy, timeSpeed: 0.25, warpStrength: 1.0, grainAmount: 0.1, contrast: 1.5, saturation: 1.0, zoom: 0.9 },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'faultyTerminal',
    name: 'Faulty Terminal',
    propDefs: {
      tint: { type: 'color', default: CI.hotPink, label: 'Tint' },
      scale: { type: 'number', min: 0.1, max: 3, step: 0.1, default: 1, label: 'Scale' },
      timeScale: { type: 'number', min: 0, max: 2, step: 0.05, default: 0.3, label: 'Time Scale' },
      scanlineIntensity: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.3, label: 'Scanline Intensity' },
      glitchAmount: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Glitch Amount' },
      flickerAmount: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Flicker Amount' },
      curvature: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.2, label: 'Curvature' },
      brightness: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Brightness' },
      mouseReact: { type: 'boolean', default: true, label: 'Mouse React' },
    },
    brandDefaults: { tint: CI.hotPink, scale: 1, timeScale: 0.3, scanlineIntensity: 0.3, glitchAmount: 1, flickerAmount: 1, curvature: 0.2, brightness: 1, mouseReact: true },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'rippleGrid',
    name: 'Ripple Grid',
    propDefs: {
      gridColor: { type: 'color', default: CI.hotPink, label: 'Grid Color' },
      rippleIntensity: { type: 'number', min: 0, max: 0.3, step: 0.01, default: 0.05, label: 'Ripple Intensity' },
      gridSize: { type: 'number', min: 2, max: 30, step: 1, default: 10, label: 'Grid Size' },
      gridThickness: { type: 'number', min: 1, max: 50, step: 1, default: 15, label: 'Grid Thickness' },
      glowIntensity: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.1, label: 'Glow Intensity' },
      opacity: { type: 'number', min: 0, max: 1, step: 0.05, default: 1.0, label: 'Opacity' },
      enableRainbow: { type: 'boolean', default: false, label: 'Rainbow' },
      mouseInteraction: { type: 'boolean', default: true, label: 'Mouse Interaction' },
    },
    brandDefaults: { gridColor: CI.hotPink, rippleIntensity: 0.05, gridSize: 10, gridThickness: 15, glowIntensity: 0.1, opacity: 1.0, enableRainbow: false, mouseInteraction: true },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'dotGrid',
    name: 'Dot Grid',
    propDefs: {
      baseColor: { type: 'color', default: CI.darkCharcoal, label: 'Base Color' },
      activeColor: { type: 'color', default: CI.hotPink, label: 'Active Color' },
      dotSize: { type: 'number', min: 4, max: 32, step: 1, default: 16, label: 'Dot Size' },
      gap: { type: 'number', min: 8, max: 64, step: 1, default: 32, label: 'Gap' },
      proximity: { type: 'number', min: 50, max: 500, step: 10, default: 150, label: 'Proximity' },
      shockRadius: { type: 'number', min: 50, max: 500, step: 10, default: 250, label: 'Shock Radius' },
      shockStrength: { type: 'number', min: 0, max: 20, step: 0.5, default: 5, label: 'Shock Strength' },
    },
    brandDefaults: { baseColor: CI.darkCharcoal, activeColor: CI.hotPink, dotSize: 16, gap: 32, proximity: 150, shockRadius: 250, shockStrength: 5 },
    enabledForProduction: false,
    requiresWebGL: false,
  },
  {
    id: 'threads',
    name: 'Threads',
    propDefs: {
      color: { type: 'rgbArray', default: [0.91, 0.27, 0.49], label: 'Color' },
      amplitude: { type: 'number', min: 0, max: 3, step: 0.1, default: 1, label: 'Amplitude' },
      distance: { type: 'number', min: 0, max: 5, step: 0.1, default: 0, label: 'Distance' },
      enableMouseInteraction: { type: 'boolean', default: false, label: 'Mouse Interaction' },
    },
    brandDefaults: { color: [0.91, 0.27, 0.49], amplitude: 1, distance: 0, enableMouseInteraction: false },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'hyperspeed',
    name: 'Hyperspeed',
    propDefs: {},
    brandDefaults: {},
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'iridescence',
    name: 'Iridescence',
    propDefs: {
      color: { type: 'rgbArray', default: [0.91, 0.27, 0.49], label: 'Color' },
      speed: { type: 'number', min: 0, max: 5, step: 0.1, default: 1.0, label: 'Speed' },
      amplitude: { type: 'number', min: 0, max: 1, step: 0.01, default: 0.1, label: 'Amplitude' },
      mouseReact: { type: 'boolean', default: true, label: 'Mouse React' },
    },
    brandDefaults: { color: [0.91, 0.27, 0.49], speed: 1.0, amplitude: 0.1, mouseReact: true },
    enabledForProduction: true,
    requiresWebGL: true,
  },
  {
    id: 'waves',
    name: 'Waves',
    propDefs: {
      lineColor: { type: 'color', default: CI.hotPink, label: 'Line Color' },
      backgroundColor: { type: 'color', default: CI.darkNavy, label: 'Background Color' },
      waveSpeedX: { type: 'number', min: 0, max: 0.1, step: 0.001, default: 0.0125, label: 'Wave Speed X' },
      waveSpeedY: { type: 'number', min: 0, max: 0.05, step: 0.001, default: 0.005, label: 'Wave Speed Y' },
      waveAmpX: { type: 'number', min: 0, max: 100, step: 1, default: 32, label: 'Wave Amp X' },
      waveAmpY: { type: 'number', min: 0, max: 100, step: 1, default: 16, label: 'Wave Amp Y' },
      xGap: { type: 'number', min: 2, max: 30, step: 1, default: 10, label: 'X Gap' },
      yGap: { type: 'number', min: 2, max: 64, step: 1, default: 32, label: 'Y Gap' },
      friction: { type: 'number', min: 0.8, max: 0.99, step: 0.005, default: 0.925, label: 'Friction' },
      tension: { type: 'number', min: 0, max: 0.05, step: 0.001, default: 0.005, label: 'Tension' },
    },
    brandDefaults: { lineColor: CI.hotPink, backgroundColor: CI.darkNavy, waveSpeedX: 0.0125, waveSpeedY: 0.005, waveAmpX: 32, waveAmpY: 16, xGap: 10, yGap: 32, friction: 0.925, tension: 0.005 },
    enabledForProduction: true,
    requiresWebGL: false,
  },
  {
    id: 'gridDistortion',
    name: 'Grid Distortion',
    propDefs: {
      grid: { type: 'number', min: 3, max: 30, step: 1, default: 15, label: 'Grid' },
      mouse: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.1, label: 'Mouse' },
      strength: { type: 'number', min: 0, max: 1, step: 0.05, default: 0.15, label: 'Strength' },
      relaxation: { type: 'number', min: 0.5, max: 1, step: 0.05, default: 0.9, label: 'Relaxation' },
    },
    brandDefaults: { grid: 15, mouse: 0.1, strength: 0.15, relaxation: 0.9 },
    enabledForProduction: false,
    requiresWebGL: true,
  },
  {
    id: 'shapeGrid',
    name: 'Shape Grid',
    propDefs: {
      borderColor: { type: 'color', default: CI.darkCharcoal, label: 'Border Color' },
      hoverFillColor: { type: 'color', default: CI.hotPink, label: 'Hover Fill Color' },
      squareSize: { type: 'number', min: 10, max: 100, step: 5, default: 40, label: 'Square Size' },
      speed: { type: 'number', min: 0, max: 5, step: 0.1, default: 1, label: 'Speed' },
      direction: { type: 'select', options: ['right', 'left', 'up', 'down', 'diagonal'], default: 'right', label: 'Direction' },
      shape: { type: 'select', options: ['square', 'circle', 'triangle', 'diamond'], default: 'square', label: 'Shape' },
    },
    brandDefaults: { borderColor: CI.darkCharcoal, hoverFillColor: CI.hotPink, squareSize: 40, speed: 1, direction: 'right', shape: 'square' },
    enabledForProduction: false,
    requiresWebGL: false,
  },
];

export function getProductionBackgrounds(): BackgroundEntry[] {
  return backgrounds.filter((b) => b.enabledForProduction);
}

export function getRandomProductionBackground(): BackgroundEntry {
  const enabled = getProductionBackgrounds();
  return enabled[Math.floor(Math.random() * enabled.length)];
}
